import { useEffect, useMemo, useState } from 'react';
import { graphql } from 'react-relay';

// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { queryInterop } from '@confluence/apollo-relay-interop/entry-points/query.ts';
import { markErrorAsHandled } from '@confluence/graphql/entry-points/markErrorAsHandled.ts';

import type {
	useCreatorInfoQuery$data,
	useCreatorInfoQuery$variables,
} from './__generated__/useCreatorInfoQuery.graphql.ts';

export type CreatorInfo = {
	displayName: string;
	avatarUrl: string;
};

export type CreatorInfoMap = Map<string, CreatorInfo>;

const CreatorInfoQuery = graphql`
	query useCreatorInfoQuery($accountIds: [ID!]!) {
		users(accountIds: $accountIds) {
			accountId
			name
			picture
		}
	}
`;

export const useCreatorInfo = (accountIds: string[]): CreatorInfoMap => {
	const [creatorInfoMap, setCreatorInfoMap] = useState<CreatorInfoMap>(new Map());

	const uniqueIds = useMemo(() => [...new Set(accountIds.filter(Boolean))], [accountIds]);

	useEffect(() => {
		if (uniqueIds.length === 0) {
			setCreatorInfoMap(new Map());
			return;
		}

		let cancelled = false;

		void (async () => {
			try {
				const { data, error } = await queryInterop<
					useCreatorInfoQuery$data,
					useCreatorInfoQuery$variables
				>({
					query: CreatorInfoQuery,
					variables: { accountIds: uniqueIds },
					interopApolloAggClient: true,
				});

				if (cancelled) {
					return;
				}

				error?.graphQLErrors?.forEach((e) => markErrorAsHandled(e));

				const map: CreatorInfoMap = new Map();
				if (data?.users) {
					for (const user of data.users) {
						if (user?.accountId && user.name) {
							map.set(user.accountId, {
								displayName: user.name,
								avatarUrl: user.picture || `/wiki/aa-avatar/${user.accountId}`,
							});
						}
					}
				}
				setCreatorInfoMap(map);
			} catch (err) {
				// Non-critical: preview will just not show author info
				if (err instanceof Error) {
					markErrorAsHandled(err);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [uniqueIds]);

	return creatorInfoMap;
};
